import { ActivityEntityName } from '../../../entities/shared/activity.entity.name';
import { MetadataEntityData } from './model/metadata.entity.data';
import { MetadataEntityFieldData } from './model/metadata.entity.field.data';
import { MetadataEntityFieldType } from './model/metadata.entity.field.types';
import { EntityFieldSensitivity } from './model/entity.field.sensitivity';
import { getMetadataEntityFieldType } from './utilities/get.entity.field.type';
import { resolveEmbeddedLevels } from './utilities/resolve.embedded.levels';

type EntitySensitivityMap = Map<EntityFieldSensitivity, Set<string>>;

export class EntityFieldSensitivityRegistry {

    private static readonly fields = new Map<ActivityEntityName, EntitySensitivityMap>();

    /** Registers sensitive root and embedded fields using absolute paths for descendant fields. */
    public static registerMetadata(metadata: MetadataEntityData): void {

        if (!metadata?.type || !metadata.fields?.length) return;

        this.registerFields(metadata.type, metadata.fields);

        for (const level of resolveEmbeddedLevels(metadata)) {
            this.registerFields(metadata.type, level.metadata.fields, level.absolutePath);
        }
    }

    public static getFields(entity: ActivityEntityName, sensitivity: EntityFieldSensitivity): string[] {

        return [...(this.fields.get(entity)?.get(sensitivity) ?? [])];
    }

    public static getFieldsForSensitivities(entity: ActivityEntityName, sensitivities: EntityFieldSensitivity[]): string[] {

        const fields = new Set<string>();
        for (const sensitivity of sensitivities ?? []) {
            for (const field of this.getFields(entity, sensitivity)) {
                fields.add(field);
            }
        }

        return [...fields];
    }

    public static hasField(entity: ActivityEntityName, field: string, sensitivities: EntityFieldSensitivity[]): boolean {

        return this.getFieldsForSensitivities(entity, sensitivities).some(sensitiveField => matchesPath(field, sensitiveField));
    }

    private static registerFields(entity: ActivityEntityName, fields: MetadataEntityFieldData[], prefix?: string): void {

        for (const field of fields ?? []) {
            const path = joinPath(prefix, field.path ?? field.name);
            this.registerField(entity, field, path, prefix);

            const type = getMetadataEntityFieldType(field);
            const childMetadata = field.typeData as MetadataEntityData;
            if (type === MetadataEntityFieldType.EmbeddedEntity && childMetadata?.fields?.length) {
                this.registerFields(entity, childMetadata.fields, path);
            }
        }
    }

    private static registerField(entity: ActivityEntityName, field: MetadataEntityFieldData, path: string, prefix?: string): void {

        const sensitivities = Array.isArray(field.sensitivity)
            ? field.sensitivity
            : field.sensitivity ? [field.sensitivity] : [];

        if (!sensitivities.length) return;

        for (const sensitivity of sensitivities) {
            this.addField(entity, sensitivity, path);
            this.addField(entity, sensitivity, joinPath(prefix, field.name));
            this.addField(entity, sensitivity, joinPath(prefix, field.attributeFilterName));
            this.addField(entity, sensitivity, joinPath(prefix, field.publicApi?.name));
        }
    }

    private static addField(entity: ActivityEntityName, sensitivity: EntityFieldSensitivity, field: string): void {

        if (!field) return;
        let entityMap = this.fields.get(entity);
        if (!entityMap) {
            entityMap = new Map<EntityFieldSensitivity, Set<string>>();
            this.fields.set(entity, entityMap);
        }

        let fields = entityMap.get(sensitivity);
        if (!fields) {
            fields = new Set<string>();
            entityMap.set(sensitivity, fields);
        }

        fields.add(field);
    }
}

function joinPath(prefix: string, path: string): string {

    if (!path) return null;
    return prefix ? `${prefix}.${path}` : path;
}

function matchesPath(field: string, sensitiveField: string): boolean {

    return field === sensitiveField || field.startsWith(`${sensitiveField}.`);
}
